
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

interface ProgressData {
  year: string;
  target: number;
  realisasi: number;
  percentage: number;
  activities: string[];
  challenges: string[];
  achievements: string[];
}

export default function RehabMangrovePage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'impact' | 'methodology'>('overview');

  const progressData: ProgressData[] = [
    {
      year: '2021',
      target: 250,
      realisasi: 187,
      percentage: 75,
      activities: ['Site assessment and preparation in 15 locations', 'Community mobilization and training', 'Initial planting of 187 hectares'],
      challenges: ['Site accessibility issues', 'Community skepticism', 'Seasonal weather constraints'],
      achievements: ['187 hectares successfully planted', '12,408 tons CO2 sequestration potential', 'Formation of 8 guardian groups']
    },
    {
      year: '2022',
      target: 400,
      realisasi: 356,
      percentage: 89,
      activities: ['Expanded restoration to 28 sites', 'Advanced nursery establishment', 'Community guardian training programs'],
      challenges: ['Scaling quality with quantity', 'Equipment procurement delays', 'Maintaining survival rates'],
      achievements: ['356 hectares restored', '85% average survival rate', 'Established 15 nursery sites']
    },
    {
      year: '2023',
      target: 550,
      realisasi: 523,
      percentage: 95,
      activities: ['Scientific monitoring system implementation', 'Biodiversity enhancement programs', 'Carbon measurement initiatives'],
      challenges: ['Climate variability impacts', 'Pest and disease management', 'Long-term sustainability planning'],
      achievements: ['523 hectares completed', '87% survival rate achieved', '23 species diversity maintained']
    },
    {
      year: '2024',
      target: 700,
      realisasi: 678,
      percentage: 97,
      activities: ['Advanced restoration techniques', 'Ecosystem services quantification', 'Community ownership transfer'],
      challenges: ['Ensuring genetic diversity', 'Climate adaptation strategies', 'Measuring ecosystem services'],
      achievements: ['678 hectares restored', '89% survival rate', '35 community-led protection initiatives']
    }
  ];

  const impactMetrics = [
    { label: 'Total Area Restored', value: '1,744', unit: 'Hectares', icon: 'ri-plant-line', color: '#228B22' },
    { label: 'Average Survival Rate', value: '87%', unit: 'Success Rate', icon: 'ri-heart-pulse-line', color: '#32CD32' },
    { label: 'Carbon Sequestered', value: '12,408', unit: 'Tons CO2', icon: 'ri-leaf-line', color: '#006400' },
    { label: 'Communities Involved', value: '156', unit: 'Groups', icon: 'ri-team-line', color: '#90EE90' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#228B22] to-[#32CD32] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-plant-line text-3xl"></i>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Rehabilitasi Mangrove Program
              </h1>
              <p className="text-xl sm:text-2xl mb-8 opacity-90">
                Scientific mangrove ecosystem restoration for coastal protection and climate resilience
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#228B22] whitespace-nowrap"
                >
                  <i className="ri-download-line mr-2"></i>
                  Restoration Guide
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#228B22] whitespace-nowrap"
                >
                  <i className="ri-map-line mr-2"></i>
                  View Sites
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { id: 'overview', label: 'Program Overview', icon: 'ri-eye-line' },
                  { id: 'progress', label: 'Progress & Targets', icon: 'ri-bar-chart-line' },
                  { id: 'impact', label: 'Impact Assessment', icon: 'ri-award-line' },
                  { id: 'methodology', label: 'Methodology', icon: 'ri-file-text-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#228B22] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Rehabilitasi Mangrove Program Overview
                  </h2>
                  <p className="text-lg text-gray-600">
                    Scientific restoration of mangrove ecosystems for coastal protection and climate resilience
                  </p>
                </div>

                {/* Storytelling Section */}
                <div className="mb-16">
                  <div className="bg-gradient-to-r from-[#228B22]/10 to-[#32CD32]/10 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#228B22]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-seedling-line text-2xl text-[#228B22]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#333333]">From Barren Coast to Thriving Ecosystem</h3>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        "Five years ago, this was just mud and debris," says Pak Rahman, pointing to a lush mangrove forest stretching along Teluk Meranti's coastline. The transformation is remarkable—where once waves crashed directly onto eroding shores, now a green barrier of mangroves provides natural protection while supporting a thriving ecosystem.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div>
                          <img
                            src="https://public.readdy.ai/ai/img_res/85eab3f9310e36b18f41499cceaadf75.jpg"
                            alt="Mangrove Restoration in Progress"
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                          <p className="text-sm text-gray-600 italic">
                            Pak Rahman inspecting restored mangrove forest at Teluk Meranti, where survival rates exceed 89%
                          </p>
                        </div>
                        
                        <div className="flex flex-col justify-center">
                          <blockquote className="border-l-4 border-[#228B22] pl-6 mb-6">
                            <p className="text-lg text-gray-700 italic mb-4">
                              "Now my grandchildren play here safely, and the fish have returned. This forest protects our village from storms and provides for our families."
                            </p>
                            <footer className="text-[#228B22] font-bold">— Pak Rahman Hakim, Community Guardian</footer>
                          </blockquote>
                          
                          <p className="text-gray-700 leading-relaxed">
                            Pak Rahman's story represents the success of M4CR's scientific approach to mangrove restoration—combining community ownership with rigorous ecological methods to achieve 87% survival rates.
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-[#333333] mb-3">The Science of Success: 87% Survival Rates</h5>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          M4CR's restoration program achieves exceptional survival rates through scientific site selection, species-specific cultivation, and community guardian networks. Each restoration site undergoes 18-month pre-assessment covering hydrology, soil chemistry, and tidal patterns.
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-[#228B22] mb-1">1,744</div>
                            <div className="text-xs text-gray-600">Hectares restored</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#32CD32] mb-1">87%</div>
                            <div className="text-xs text-gray-600">Survival rate</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#006400] mb-1">12,408</div>
                            <div className="text-xs text-gray-600">Tons CO2 stored</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Journalist Report Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#32CD32]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-newspaper-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#333333]">Field Report: Engineering Ecosystem Recovery</h3>
                        <p className="text-gray-600">How M4CR achieves 87% mangrove survival rates through scientific restoration</p>
                      </div>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <div className="bg-[#F8F9FA] border-l-4 border-[#32CD32] p-6 mb-6 rounded-r-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Scientific Investigation</strong> • December 2024 • M4CR Research Team
                        </p>
                        <h4 className="text-lg font-bold text-[#333333] mb-3">
                          The Methodology Behind 87% Success: Breaking Down Restoration Science
                        </h4>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        <strong>KARAWANG, West Java</strong> — Dr. Marina Sari kneels in ankle-deep tidal water, examining the root system of a two-year-old Rhizophora apiculata. The tree, part of M4CR's restoration program, represents more than successful planting—it embodies a scientific revolution in mangrove restoration that has achieved 87% survival rates across 1,744 hectares.
                      </p>
                      
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-green-800 mb-4">The Site Selection Protocol</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <h6 className="font-semibold text-green-700 mb-3">Pre-Assessment (18 months)</h6>
                            <ul className="space-y-2 text-sm text-green-700">
                              <li>• Hydrology and tidal pattern analysis</li>
                              <li>• Soil chemistry and salinity testing</li>
                              <li>• Sediment stability evaluation</li>
                              <li>• Biodiversity baseline surveys</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-green-700 mb-3">Species Selection</h6>
                            <ul className="space-y-2 text-sm text-green-700">
                              <li>• 12 native species cultivation</li>
                              <li>• Microhabitat matching protocols</li>
                              <li>• Genetic diversity optimization</li>
                              <li>• Climate adaptation screening</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-green-700 mb-3">Community Integration</h6>
                            <ul className="space-y-2 text-sm text-green-700">
                              <li>• Guardian network establishment</li>
                              <li>• Local stewardship training</li>
                              <li>• Threat monitoring systems</li>
                              <li>• Sustainable use planning</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <blockquote className="border-l-4 border-[#228B22] pl-6 mb-6 bg-[#F8F9FA] p-4 rounded-r-lg">
                        <p className="text-lg text-gray-700 italic mb-4">
                          "We don't just plant trees—we engineer ecosystem recovery. Every seedling carries the genetic diversity and resilience needed to restore Indonesia's coastlines."
                        </p>
                        <footer className="text-[#228B22] font-bold">— Dr. Marina Sari, Chief Restoration Scientist</footer>
                      </blockquote>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Program Description */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Program Objectives</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#228B22]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#228B22]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Ecosystem Restoration</h4>
                          <p className="text-gray-600 text-sm">
                            Restore degraded mangrove ecosystems using scientific approaches and native species
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#32CD32]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#32CD32]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Coastal Protection</h4>
                          <p className="text-gray-600 text-sm">
                            Provide natural barriers against storm surges, erosion, and sea-level rise
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#006400]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#006400]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Carbon Sequestration</h4>
                          <p className="text-gray-600 text-sm">
                            Capture and store atmospheric carbon dioxide in mangrove biomass and sediments
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#90EE90]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#90EE90]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Biodiversity Conservation</h4>
                          <p className="text-gray-600 text-sm">
                            Restore critical habitat for marine and terrestrial species
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Restoration Activities</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Site Assessment</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Comprehensive ecological evaluation and restoration planning
                        </p>
                        <div className="text-xs text-[#228B22] font-medium">47 parameters assessed per site</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Species Cultivation</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Nursery production and cultivation of 12 native mangrove species
                        </p>
                        <div className="text-xs text-[#32CD32] font-medium">1.46M seedlings produced</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Restoration Planting</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Scientific planting protocols optimized for survival and growth
                        </p>
                        <div className="text-xs text-[#006400] font-medium">1,744 hectares restored</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Monitoring & Protection</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Long-term monitoring and community-based protection systems
                        </p>
                        <div className="text-xs text-[#90EE90] font-medium">156 guardian groups active</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Stories */}
                <div className="bg-gradient-to-r from-[#228B22]/10 to-[#32CD32]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Stories</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20coastal%20community%20leader%20portrait%2C%20middle-aged%20man%20with%20confident%20expression%2C%20mangrove%20forest%20background%2C%20professional%20headshot%20style%2C%20natural%20lighting%2C%20conservation%20leadership&width=60&height=60&seq=testimonial-3&orientation=squarish"
                          alt="Pak Rahman"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Pak Rahman Hakim</div>
                          <div className="text-sm text-gray-600">Community Guardian, Teluk Meranti</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "Our coastline was disappearing every year. Now with healthy mangroves, we have natural protection and the fish populations are returning. My children have a future here."
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20female%20marine%20biologist%20portrait%2C%20professional%20scientist%20with%20confident%20smile%20%2C%20mangrove%20research%20station%20background%2C%20scientific%20expertise%2C%20natural%20lighting%2C%20conservation%20research&width=60&height=60&seq=testimonial-4&orientation=squarish"
                          alt="Dr. Marina"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Dr. Marina Sari</div>
                          <div className="text-sm text-gray-600">Chief Restoration Scientist</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "The 87% survival rate we've achieved proves that science-based restoration works. Each site teaches us more about optimizing mangrove recovery for maximum ecological impact."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Progress Tracking & Target Achievement
                  </h2>
                  <p className="text-lg text-gray-600">
                    Four-year journey of scaling scientific mangrove restoration
                  </p>
                </div>

                {/* Overall Progress Chart */}
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-12">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Annual Progress Overview</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {progressData.map((data, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-[#228B22] mb-2">{data.year}</div>
                        <div className="text-lg font-medium text-[#333333] mb-2">{data.percentage}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-[#228B22] to-[#32CD32] h-3 rounded-full transition-all duration-1000"
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {data.realisasi}/{data.target} hectares
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#F8F9FA] rounded-lg p-6">
                    <h4 className="font-bold text-[#333333] mb-4 text-center">Cumulative Achievement</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#228B22] mb-1">1,744</div>
                        <div className="text-sm text-gray-600">Total Hectares</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#32CD32] mb-1">89%</div>
                        <div className="text-sm text-gray-600">Overall Achievement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#006400] mb-1">87%</div>
                        <div className="text-sm text-gray-600">Average Survival Rate</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Annual Breakdown */}
                <div className="space-y-8">
                  {progressData.map((data, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-[#228B22] to-[#32CD32] rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                              {data.year}
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-[#333333]">{data.percentage}%</div>
                              <div className="text-gray-600">Achievement Rate</div>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Target:</span>
                              <span className="font-bold text-[#333333]">{data.target} hectares</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Restored:</span>
                              <span className="font-bold text-[#228B22]">{data.realisasi} hectares</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-[#228B22] h-2 rounded-full"
                                style={{ width: `${data.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Key Activities</h4>
                              <ul className="space-y-2">
                                {data.activities.map((activity, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-check-line text-[#228B22] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{activity}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Challenges</h4>
                              <ul className="space-y-2">
                                {data.challenges.map((challenge, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-alert-line text-[#FF6B6B] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{challenge}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Achievements</h4>
                              <ul className="space-y-2">
                                {data.achievements.map((achievement, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-trophy-line text-[#FFD93D] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Impact Tab */}
        {activeTab === 'impact' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Impact Assessment & Outcomes
                  </h2>
                  <p className="text-lg text-gray-600">
                    Measuring the ecological and social impact of mangrove restoration
                  </p>
                </div>

                {/* Impact Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center hover:shadow-lg transition-shadow duration-300">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Ecological Impact Assessment */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Ecological Indicators</h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Mangrove Survival Rate</span>
                          <span className="font-bold text-[#228B22]">87%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#228B22] h-2 rounded-full" style={{ width: '87%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Seedling survival rate after 24 months</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Biodiversity Recovery</span>
                          <span className="font-bold text-[#32CD32]">73%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#32CD32] h-2 rounded-full" style={{ width: '73%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Species diversity compared to reference sites</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Coastal Protection</span>
                          <span className="font-bold text-[#006400]">91%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#006400] h-2 rounded-full" style={{ width: '91%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Wave energy reduction effectiveness</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Water Quality</span>
                          <span className="font-bold text-[#90EE90]">68%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#90EE90] h-2 rounded-full" style={{ width: '68%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Improvement in water quality parameters</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Climate Impact</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#228B22]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-leaf-line text-[#228B22]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Carbon Sequestration</div>
                            <div className="text-2xl font-bold text-[#228B22]">12,408</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Tons of CO2 sequestered in restored mangrove ecosystems
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#32CD32]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-shield-line text-[#32CD32]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Coastline Protected</div>
                            <div className="text-2xl font-bold text-[#32CD32]">847</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Kilometers of coastline protected from erosion and storms
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#006400]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-water-percent-line text-[#006400]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Storm Surge Reduction</div>
                            <div className="text-2xl font-bold text-[#006400]">23%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Average reduction in storm surge impact on coastal communities
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Long-term Impact */}
                <div className="bg-gradient-to-r from-[#228B22]/10 to-[#32CD32]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Long-term Impact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#228B22]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-plant-line text-2xl text-[#228B22]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Ecosystem Recovery</h4>
                      <p className="text-gray-600 text-sm">
                        Restored mangrove ecosystems now support diverse marine and terrestrial species, creating thriving coastal habitats
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#32CD32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-shield-check-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Community Protection</h4>
                      <p className="text-gray-600 text-sm">
                        156 coastal communities now benefit from natural protection against storms, erosion, and sea-level rise
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#006400]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-leaf-line text-2xl text-[#006400]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Climate Resilience</h4>
                      <p className="text-gray-600 text-sm">
                        Significant contribution to climate change mitigation through carbon sequestration and coastal adaptation
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Methodology Tab */}
        {activeTab === 'methodology' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Restoration Methodology
                  </h2>
                  <p className="text-lg text-gray-600">
                    Scientific approaches and proven techniques for successful mangrove restoration
                  </p>
                </div>

                {/* Restoration Process */}
                <div className="space-y-8 mb-12">
                  {[
                    {
                      step: 1,
                      title: 'Site Assessment & Planning',
                      description: 'Comprehensive ecological evaluation and restoration strategy development',
                      icon: 'ri-search-line',
                      color: '#228B22',
                      activities: [
                        '18-month pre-restoration monitoring and assessment',
                        'Hydrology, soil chemistry, and tidal pattern analysis',
                        'Biodiversity baseline surveys and habitat mapping',
                        'Community consultation and stakeholder engagement'
                      ]
                    },
                    {
                      step: 2,
                      title: 'Species Selection & Cultivation',
                      description: 'Native species optimization and high-quality seedling production',
                      icon: 'ri-seedling-line',
                      color: '#32CD32',
                      activities: [
                        '12 native mangrove species cultivation program',
                        'Microhabitat matching and genetic diversity optimization',
                        'Advanced nursery techniques and quality control',
                        'Climate adaptation and disease resistance screening'
                      ]
                    },
                    {
                      step: 3,
                      title: 'Scientific Planting',
                      description: 'Precision planting protocols optimized for survival and growth',
                      icon: 'ri-plant-line',
                      color: '#006400',
                      activities: [
                        'Optimal planting density and spatial arrangement',
                        'Tidal timing and seasonal considerations',
                        'Root protection and establishment techniques',
                        'Initial care and maintenance protocols'
                      ]
                    },
                    {
                      step: 4,
                      title: 'Monitoring & Protection',
                      description: 'Long-term stewardship and adaptive management systems',
                      icon: 'ri-shield-check-line',
                      color: '#90EE90',
                      activities: [
                        'Community guardian network establishment',
                        'Growth monitoring and survival rate tracking',
                        'Threat assessment and mitigation strategies',
                        'Adaptive management and continuous improvement'
                      ]
                    }
                  ].map((step, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="flex items-start space-x-6">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                          style={{ backgroundColor: step.color }}
                        >
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center mb-4">
                            <i className={`${step.icon} text-2xl mr-3`} style={{ color: step.color }}></i>
                            <h3 className="text-2xl font-bold text-[#333333]">{step.title}</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg mb-6">
                            {step.description}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {step.activities.map((activity, idx) => (
                              <div key={idx} className="flex items-start space-x-3">
                                <div 
                                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{ backgroundColor: `${step.color}20` }}
                                >
                                  <i className="ri-check-line text-xs" style={{ color: step.color }}></i>
                                </div>
                                <span className="text-gray-700 text-sm">{activity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Scientific Standards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Environmental Parameters</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#228B22]/10 rounded-full flex items-center justify-center">
                          <i className="ri-drop-line text-[#228B22]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Salinity Levels</h4>
                          <p className="text-sm text-gray-600">15-35 ppt optimal range for species selection</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#32CD32]/10 rounded-full flex items-center justify-center">
                          <i className="ri-water-percent-line text-[#32CD32]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Tidal Range</h4>
                          <p className="text-sm text-gray-600">0.5-2.5m range for optimal species matching</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#006400]/10 rounded-full flex items-center justify-center">
                          <i className="ri-test-tube-line text-[#006400]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Soil pH</h4>
                          <p className="text-sm text-gray-600">6.5-8.5 range for optimal growth conditions</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#90EE90]/10 rounded-full flex items-center justify-center">
                          <i className="ri-earth-line text-[#90EE90]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Sediment Stability</h4>
                          <p className="text-sm text-gray-600">Comprehensive stability assessment protocols</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Quality Control</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#228B22]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-line-chart-line text-[#228B22]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Survival Monitoring</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Monthly survival rate assessments and growth measurements
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#32CD32]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-plant-line text-[#32CD32]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Growth Protocols</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Standardized measurement and documentation procedures
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#006400]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-bug-line text-[#006400]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Biodiversity Assessment</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Regular biodiversity surveys and ecosystem health evaluation
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Metrics */}
                <div className="bg-[#F8F9FA] rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#228B22]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-heart-pulse-line text-2xl text-[#228B22]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Survival Rate</h4>
                      <p className="text-gray-600 text-sm mb-2">Target: &gt;80%</p>
                      <div className="text-xs text-gray-500">Current: 87%</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#32CD32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-leaf-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Carbon Storage</h4>
                      <p className="text-gray-600 text-sm mb-2">7.1 tons CO2/ha</p>
                      <div className="text-xs text-gray-500">Annual rate</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#006400]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-shield-line text-2xl text-[#006400]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Coastal Protection</h4>
                      <p className="text-gray-600 text-sm mb-2">23% wave reduction</p>
                      <div className="text-xs text-gray-500">Storm surge</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#90EE90]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-community-line text-2xl text-[#90EE90]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Community Engagement</h4>
                      <p className="text-gray-600 text-sm mb-2">156 guardian groups</p>
                      <div className="text-xs text-gray-500">Active stewards</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="py-16 bg-[#228B22] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Support Mangrove Restoration
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Join our mission to restore Indonesia's coastal ecosystems and build climate resilience for future generations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#228B22] whitespace-nowrap"
                  href="/program"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Program
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#228B22] whitespace-nowrap"
                  href="/tentang"
                >
                  <i className="ri-information-line mr-2"></i>
                  Learn More About M4CR
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
